<template>
  <b-modal :visible="open" title="Provedores de CPF/CNPJ" hide-footer @hide="onHide">
    <b-form-group label="Provedor">
      <div class="p-2 border rounded d-flex align-items-center">
        <b-form-select
          :key="selectKey"
          v-model="form.provider"
          :options="options"
          :disabled="saving || testing || loading"
          @change="markDirty"
        />
        <b-spinner v-if="loading" small class="ml-1" />
      </div>
    </b-form-group>

    <b-form-group  v-if="form.provider === 'cpfcnpj' && !isDirty && lastSavedProvider === 'cpfcnpj'" label="Consultar pacote (CPF/CNPJ)">
      <div class="d-flex align-items-center">
        <b-button variant="outline-secondary" :disabled="testing" @click="consultPackage">
          <feather-icon icon="ActivityIcon" class="mr-50" />
          Consultar pacote
        </b-button>
        <b-spinner v-if="testing" small class="ml-1" />
      </div>

      <b-alert v-if="testResult" :variant="testVariant" class="mt-2" show>
        <div class="d-flex align-items-center">
          <feather-icon
            :icon="testVariant === 'success'
              ? 'CheckCircleIcon'
              : (testVariant === 'warning' ? 'AlertTriangleIcon' : 'AlertCircleIcon')"
            class="mr-50"
          />
          <div>
            <strong>{{ testTitle }}</strong>
            <div v-if="testMessage" style="white-space: pre-line;">
              {{ testMessage }}
            </div>
          </div>
        </div>
      </b-alert>
    </b-form-group>

    <div class="d-flex">
      <b-button
        variant="primary"
        class="mr-1"
        :disabled="saving || loading || !isDirty || !form.provider"
        @click="save"
      >
        <feather-icon icon="SaveIcon" class="mr-50" /> Salvar
      </b-button>
      <b-button variant="secondary" @click="onHide">Fechar
      </b-button>
    </div>
  </b-modal>
</template>

<script>
export default {
  name: 'SystemSettingsCnpjProvider',
  props: { open: { type: Boolean, default: false } },
  data() {
    return {
      loading: false,
      saving: false,
      testing: false,
      isDirty: false,
      testResult: null,
      selectKey: 0,
      form: { provider: null },
      lastSavedProvider: null,
      options: [
        { text: 'Selecione…', value: null, disabled: true },
        { text: 'Serpro', value: 'serpro' },
        { text: 'CPF.CNPJ', value: 'cpfcnpj' },
        { text: 'Brasil API', value: 'brasilapi' },
      ],
    }
  },
  computed: {
    testVariant() {
      if (!this.testResult) return 'secondary'
      if (this.testResult.ok) return 'success'
      if (this.testResult.status === 422) return 'warning'
      if ((this.testResult.status || 0) >= 500) return 'danger'
      return 'danger'
    },
    testTitle() {
      if (!this.testResult) return ''
      const variant = this.testVariant === 'warning' ? 'Aviso' : 'Falha'
      return this.testResult.ok ? 'Consulta OK' : variant
    },
    testMessage() {
      return (this.testResult && (this.testResult.message || this.testResult.error)) || ''
    },
  },
  watch: {
    open(v) { if (v) this.fetch() },
  },
  methods: {
    extractProvider(resp) {
      const root = (resp && Object.prototype.hasOwnProperty.call(resp, 'data')) ? resp.data : resp
      const payload = (root && root.data) ? root.data : root
      let settings = payload && payload.settings
      if (settings && typeof settings === 'string') {
        try { settings = JSON.parse(settings) } catch (e) { }
      }
      return (settings && settings.provider) || null
    },

    async fetch() {
      this.loading = true
      this.testResult = null
      try {
        const res = await this.$http.get('system-settings/cnpj-provider')
        const prov = this.extractProvider(res)
        this.lastSavedProvider = prov
        this.form.provider = prov
        this.isDirty = false
        this.selectKey++
      } finally {
        this.loading = false
      }
    },

    markDirty() {
      this.isDirty = (this.form.provider !== this.lastSavedProvider)
    },

    async save() {
      if (!this.form.provider || !this.isDirty) return
      this.saving = true
      try {
        await this.$http.put('system-settings/cnpj-provider', { provider: this.form.provider })
        this.lastSavedProvider = this.form.provider
        this.isDirty = false
        this.selectKey++
      } finally {
        this.saving = false
      }
    },

    async consultPackage() {
      if (this.form.provider !== 'cpfcnpj') return
      this.testing = true
      this.testResult = null

      try {
        const res = await this.$http.get('system-settings/cnpj-provider/consult')

        const payload = (res && res.data && (res.data.data || res.data)) || {}

        const hasOk = Object.prototype.hasOwnProperty.call(payload, 'ok')
        const d = hasOk ? (payload.data || {}) : payload
        const ok = hasOk ? !!payload.ok : (d.saldo != null || d.pacote != null)

        const saldo = (d.saldo != null)
          ? d.saldo
          : (d.pacote && typeof d.pacote === 'object' ? d.pacote.saldo : null)

        const pacoteLabel = (() => {
          const v = d.pacote
          if (v == null) return '-'
          if (typeof v !== 'object') return String(v)
          return v.nome || v.name || v.id || JSON.stringify(v)
        })()

        const msg = ok
          ? 'Pacote: ' + pacoteLabel + '\n' + 'Saldo: ' + (saldo == null ? '-' : String(saldo))
          : (payload.message || 'Sem dados')

        this.testResult = { ok, status: res?.status ?? 200, message: msg }
      } catch (e) {
        const status = e?.response?.status || 0
        const rawErr = e?.response?.data || {}
        const message = rawErr.message || rawErr.error || e.message
        this.testResult = { ok: false, status, message }
      } finally {
        this.testing = false
      }
    },
    onHide() {
      if (this.isDirty) {
        this.form.provider = this.lastSavedProvider
        this.isDirty = false
        this.selectKey++
      }
      this.testResult = null
      this.$emit('onClose')
    },
  },
}
</script>
